import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  BadgeDollarSign,
  BookOpen,
  CalendarDays,
  ChevronRight,
  ClipboardList,
  CloudSun,
  Fish,
  Globe2,
  MapPin,
  Route,
  ShieldCheck,
  UsersRound,
} from "lucide-react";
import LanguageSwitcher from "../LanguageSwitcher";
import "../../styles/components/landing/ReelitProLandingPage.css";

const FEATURE_ITEMS = [
  {
    key: "share_catches",
    Icon: Fish,
  },
  {
    key: "explore_spots",
    Icon: MapPin,
  },
  {
    key: "check_conditions",
    Icon: CloudSun,
  },
  {
    key: "plan_trips",
    Icon: Route,
  },
  {
    key: "connect_anglers",
    Icon: UsersRound,
  },
];

const SCREENSHOT_CARDS = [
  {
    key: "feed",
    image: "/landing/screenshots/feed-mobile.png",
  },
  {
    key: "map",
    image: "/landing/screenshots/map-mobile.png",
  },
  {
    key: "groups",
    image: "/landing/screenshots/groups-mobile.png",
  },
  {
    key: "my_trips",
    image: "/landing/screenshots/my-trips-mobile.png",
  },
];

const TRUST_ITEMS = [
  {
    key: "data_safe",
    Icon: ShieldCheck,
  },
  {
    key: "free",
    Icon: BadgeDollarSign,
  },
  {
    key: "web_mobile",
    Icon: Globe2,
  },
];
const SEO_TOOL_LINKS = [
  { to: "/fishing-app/", key: "fishing_app", Icon: Fish },
  { to: "/fishing-catch-log-app/", key: "catch_log", Icon: ClipboardList },
  { to: "/fishing-spots-app/", key: "spots", Icon: MapPin },
  { to: "/fishing-trip-planner/", key: "trip_planner", Icon: CalendarDays },
  { to: "/fish-species-guide/", key: "species_guide", Icon: BookOpen },
  { to: "/fishing-regulations/", key: "regulations", Icon: ShieldCheck },
];
const PLAY_STORE_URL =
  "https://play.google.com/store/apps/details?id=com.reelitpro.app";
const publicUrl = process.env.PUBLIC_URL || "";

function isAndroidDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }

  if (navigator.userAgentData?.platform) {
    return /Android/i.test(navigator.userAgentData.platform);
  }

  return /Android/i.test(navigator.userAgent || "");
}

export default function ReelitProLandingPage() {
  const { t } = useTranslation();
  const [showPlayStoreCta, setShowPlayStoreCta] = useState(false);

  useEffect(() => {
    setShowPlayStoreCta(isAndroidDevice());
  }, []);

  return (
    <main className="rp-landing">
      <header className="rp-landing__header">
        <Link to="/" className="rp-landing__brand" aria-label={t("landing_brand_aria")}>
          <img
            src="/banner/logo.png"
            alt={t("landing_logo_alt")}
            className="rp-landing__brand-logo"
          />
          <span className="rp-landing__brand-wordmark">
            <span className="rp-landing__brand-script">
              {t("navbar_mobile_brand_reel_it", { defaultValue: "Reel it" })}
            </span>
            <span className="rp-landing__brand-pro">
              {t("navbar_mobile_brand_pro", { defaultValue: "PRO" })}
            </span>
          </span>
        </Link>

        <div className="rp-landing__header-actions">
          <LanguageSwitcher />
          <Link to="/login" className="rp-landing__nav-link">
            {t("landing_login_button")}
          </Link>
        </div>
      </header>

      <section className="rp-landing__hero" aria-labelledby="rp-landing-title">
        <div
          className="rp-landing__hero-bg"
          aria-hidden="true"
          style={{
            backgroundImage: `url("${publicUrl}/landing/hero/reelitpro-landing-hero.jpg")`,
          }}
        />
        <div className="rp-landing__hero-content">
          <h1 id="rp-landing-title">{t("landing_hero_title")}</h1>
          <p className="rp-landing__hero-subhead">{t("landing_hero_subhead")}</p>
          <p className="rp-landing__hero-copy">{t("landing_hero_body")}</p>
          <div className="rp-landing__hero-actions">
            <Link to="/register" className="rp-landing__primary">
              {t("landing_create_account")}
            </Link>
            <Link to="/login" className="rp-landing__secondary">
              {t("landing_login_button")}
            </Link>
            {showPlayStoreCta ? (
              <a
                className="rp-landing__secondary rp-landing__play-store"
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                {t("landing_download_google_play")}
              </a>
            ) : null}
          </div>
          <p className="rp-landing__trust-line">{t("landing_trust_line")}</p>
        </div>
      </section>

      <section className="rp-landing__feature-strip" aria-label={t("landing_features_aria")}>
        {FEATURE_ITEMS.map(({ key, Icon }) => (
          <span key={key}>
            <Icon size={16} strokeWidth={2.4} aria-hidden="true" />
            {t(`landing_feature_${key}`)}
          </span>
        ))}
      </section>

      <section className="rp-landing__screens">
        <div className="rp-landing__section-heading">
          <p>{t("landing_screens_eyebrow")}</p>
          <h2>{t("landing_screens_title")}</h2>
        </div>
        <div className="rp-landing__screen-grid">
          {SCREENSHOT_CARDS.map((card) => (
            <article className="rp-landing__screen-card" key={card.key}>
              <div className="rp-landing__phone-frame">
                <img
                  src={card.image}
                  alt={t(`landing_screenshot_${card.key}_alt`)}
                  loading="lazy"
                />
              </div>
              <div className="rp-landing__screen-copy">
                <span>{t(`landing_screenshot_${card.key}_label`)}</span>
                <h3>{t(`landing_screenshot_${card.key}_title`)}</h3>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="rp-landing__community">
        <div className="rp-landing__community-copy">
          <div>
            <p>{t("landing_community_eyebrow")}</p>
            <h2>{t("landing_community_title")}</h2>
          </div>
          <p>{t("landing_community_body")}</p>
        </div>
        <div className="rp-landing__community-art" aria-hidden="true">
          <img src="/landing/community/anglers.png" alt="" loading="lazy" />
        </div>
      </section>

      <section className="rp-landing__seo-tools" aria-labelledby="rp-landing-tools-title">
        <div className="rp-landing__tools-panel">
          <div className="rp-landing__tools-head">
            <div className="rp-landing__section-heading">
              <p>{t("landing_tools_eyebrow")}</p>
              <h2 id="rp-landing-tools-title">{t("landing_tools_title")}</h2>
              <span className="rp-landing__tools-subtitle">{t("landing_tools_subtitle")}</span>
            </div>
            <div className="rp-landing__tools-art" aria-hidden="true">
              <img src="/banner/Landing-page-icon.png" alt="" loading="lazy" />
            </div>
          </div>

          <div className="rp-landing__tool-list">
            {SEO_TOOL_LINKS.map(({ to, key, Icon }) => (
              <Link className="rp-landing__tool-card" to={to} key={to}>
                <span className="rp-landing__tool-icon" aria-hidden="true">
                  <Icon size={20} strokeWidth={2.35} />
                </span>
                <span className="rp-landing__tool-copy">
                  <strong>{t(`landing_tools_${key}_label`)}</strong>
                  <span>{t(`landing_tools_${key}_body`)}</span>
                </span>
                <ChevronRight
                  className="rp-landing__tool-arrow"
                  size={19}
                  strokeWidth={2.6}
                  aria-hidden="true"
                />
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="rp-landing__final-cta">
        <h2>{t("landing_final_cta_title")}</h2>
        <p>{t("landing_final_cta_body")}</p>
        <Link to="/register" className="rp-landing__primary">
          {t("landing_create_account")}
        </Link>
      </section>

      <footer className="rp-landing__footer">
        <div className="rp-landing__footer-trust">
          {TRUST_ITEMS.map(({ key, Icon }) => (
            <div className="rp-landing__trust-item" key={key}>
              <Icon size={17} strokeWidth={2.35} aria-hidden="true" />
              <span>
                <strong>{t(`landing_footer_trust_${key}`)}</strong>
                <small>{t(`landing_footer_trust_${key}_subtitle`)}</small>
              </span>
            </div>
          ))}
        </div>
        <nav className="rp-landing__footer-links" aria-label={t("landing_footer_legal_aria")}>
          <Link to="/legal/terms">{t("legal_document_terms_title")}</Link>
          <Link to="/legal/privacy">{t("legal_document_privacy_title")}</Link>
          <Link to="/legal/attributions">{t("legal_document_attributions_title")}</Link>
        </nav>
      </footer>
    </main>
  );
}
